import { useEffect, useRef, useState } from 'react'

import { useFetchToolsAndSupport } from 'api/queries/useFetchToolsAndSupport'
import Bubble from 'components/common/bubble/Bubble'
import { Pagination } from 'components/pagination/Pagination'
import { useDebounce } from 'hooks/useDebounce'
import { AnalyticsActionType } from 'types/analytics.type'
import { TasSortField } from 'types/TasSortEnum'
import { trackAnalytics } from 'utils/analytics'

import styles from './AllWorkTools.module.scss'
import Search from './search/Search'
import Table from './table/Table'
import cogsGif from '../../assets/images/allWorkTools/tools-cogs.gif'
import { AppData } from '../../types/appData'
import TitleText from '../common/titleText/TitleText'

const AllWorkTools = () => {
  const initialAppData = useRef<AppData[]>([])
  const [appData, setAppData] = useState<AppData[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [sortNameOrder, setSortNameOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [sortCategoryOrder, setSortCategoryOrder] = useState<'ASC' | 'DESC'>('ASC')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sorting, setSorting] = useState<{ sortBy?: string; direction?: 'ASC' | 'DESC' }>()
  const filteredAppsArray: AppData[] = []
  const [page, setPage] = useState(0)
  const [pageSize, setPerPage] = useState(10)

  const debouncedSearch = useDebounce(searchInput, 500)

  const title = 'All Work Tools'

  const placeholderText = 'Search by name, category or description'

  // use effect for category sorting
  useEffect(() => {
    setSorting({ sortBy: TasSortField.CATEGORY, direction: sortCategoryOrder })
    setPage(0)
  }, [sortCategoryOrder])

  // use effect for name sorting
  useEffect(() => {
    setSorting({ sortBy: TasSortField.NAME, direction: sortNameOrder })
    setPage(0)
  }, [sortNameOrder])

  const { data: toolsAndSupportData } = useFetchToolsAndSupport({
    params: {
      limit: pageSize,
      search: debouncedSearch ?? '',
      page: page + 1,
      sortField: [sorting?.sortBy || TasSortField.NAME],
      sortDirection: [sorting?.direction || 'ASC'],
    },
  })

  useEffect(() => {
    if (debouncedSearch !== '') {
      trackAnalytics({
        type: AnalyticsActionType.action,
        payload: {
          action: 'pg_act_toolsandsupport_allworktools_search',
          params: [
            {
              key: 'search',
              value: debouncedSearch,
            },
          ],
        },
      })
    }
  }, [debouncedSearch])

  const openIntercomMessage = () => {
    if (window.Intercom) {
      window.Intercom('show')
    }
  }

  const text = [
    'Have a question? Pop a message into ',
    <span
      key="intercom"
      onClick={openIntercomMessage}
      style={{ cursor: 'pointer', color: 'black', textDecoration: 'underline' }}
    >
      Intercom
    </span>,
    '.',
  ]

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value)
    setPage(0)
    if (event.target.value.length === 0) {
      setAppData(initialAppData.current)
    } else {
      searchAppData(appData)
    }
  }

  function searchAppData(appData: AppData[]) {
    appData.forEach((appDataItem: AppData) => {
      if (appDataItem.name === undefined) return
      const appName = appDataItem?.name.toLowerCase()
      if (appName.includes(searchInput.toLowerCase())) {
        filteredAppsArray.push(appDataItem)
      }
    })

    setAppData(filteredAppsArray)
  }

  //const filteredTools = ['KO Total Rewards Hub', 'MAP (Marketing & Agency Platform)']

  return (
    <div className={styles.container}>
      <TitleText
        containerClassName={styles.titleContainer}
        titleTag="h2"
        titleClassName={styles.title}
        textTag="p"
        textClassName={styles.text}
        title={title}
        text={text}
        titleImage={cogsGif}
        imageClass="w-20 h-20"
      >
        <Bubble
          className="absolute right-[42px] top-0 -translate-y-full"
          color="#EBFF00"
          text="High-tech toolbox at your service"
        />
        <Search
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          placeholderText={placeholderText}
          handleSearchChange={handleSearchChange}
        />
      </TitleText>
      <div className={styles.tableContainer}>
        <Table
          appData={(toolsAndSupportData as any)?.records}
          setAppData={setAppData}
          setSortNameOrder={setSortNameOrder}
          sortNameOrder={sortNameOrder}
          setSortCategoryOrder={setSortCategoryOrder}
          sortCategoryOrder={sortCategoryOrder}
        />
      </div>
      <div className={styles.paginationContainer}>
        <Pagination
          totalItems={(toolsAndSupportData as any)?.total ?? 0}
          pageSize={10}
          currentPage={page}
          onPageChanged={page => setPage(page)}
          itemsPerPageChanged={itemsPerPage => setPerPage(itemsPerPage)}
        />
      </div>
    </div>
  )
}

export default AllWorkTools
